import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { FormProvider } from 'react-hook-form'
import {
  Button,
  ChevronLeftIcon,
  H2,
  LoadingDots,
  Span,
  Text,
} from '@/components/atoms'
import {
  ActionModal,
  IdCard,
  Stepper,
  StickyFooter,
} from '@/components/molecules'
import type { StepperItem } from '@/components/molecules'
import {
  CARD_FLIP_PERSPECTIVE,
  CARD_FLIP_TRANSFORM,
} from '@/shared/constants/claims'
import { ROUTES } from '@/shared/constants/routes'
import { useClaimsPetPage } from './page.claims.pet.hook'
import { ClaimsPetStepOnePage } from './step-one/page.claims.pet-step-one'
import { ClaimsPetStepTwoPage } from './step-two/page.claims.pet-step-two'

export function ClaimsPetPage() {
  const { t } = useTranslation()
  const {
    formMethods,
    currentStep,
    isFlipping,
    isLastStep,
    hasSelectedPet,
    card,
    isDataLoading,
    isSubmitting,
    submitErrorKey,
    isOffline,
    submitModal,
    handleNext,
    handleBack,
    handleCancel,
  } = useClaimsPetPage()

  const steps: StepperItem[] = [
    {
      label: t('claims.pet.stepOne.label'),
      children: <ClaimsPetStepOnePage />,
    },
    {
      label: t('claims.pet.stepTwo.label'),
      children: (
        <ClaimsPetStepTwoPage
          submitErrorKey={submitErrorKey}
          isOffline={isOffline}
        />
      ),
    },
  ]

  if (isDataLoading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-white">
        <LoadingDots className="loading-xl" />
      </div>
    )
  }

  return (
    <FormProvider {...formMethods}>
      <div className="flex min-h-dvh flex-col bg-white">
        <div className="mx-auto w-full max-w-266.5 px-6 pt-6 md:px-0">
          <Link
            to={ROUTES.CLAIMS}
            className="text-indigo-base flex w-fit items-center gap-1 text-sm"
            data-testid="claim-form-back-to-claims"
            aria-label={t('claims.pet.back')}
          >
            <ChevronLeftIcon />
          </Link>
        </div>
        {/* overflow-clip (not hidden): a hidden container is still scrollable, and
          focusing an input made the browser scroll it towards the off-screen step */}
        <div className="mx-auto flex w-full max-w-266.5 flex-1 flex-col overflow-clip px-6 pt-6 pb-0 md:flex-row md:gap-8 md:px-0 md:pt-8 md:pb-32">
          <div className="z-10 hidden flex-col bg-white md:flex md:max-w-117 md:justify-start">
            {/* Grid rows 0fr → 1fr animates the height, so the title below slides down smoothly */}
            <div
              className={`grid transition-[grid-template-rows] duration-500 ease-out ${hasSelectedPet ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
              <div className="min-h-0 overflow-hidden">
                <div
                  className={`pb-6 transition duration-500 ease-out ${hasSelectedPet ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
                >
                  <div
                    className="transition-transform duration-300"
                    style={{
                      perspective: CARD_FLIP_PERSPECTIVE,
                      transformStyle: 'preserve-3d',
                      transform: isFlipping
                        ? CARD_FLIP_TRANSFORM.FLIPPING
                        : CARD_FLIP_TRANSFORM.IDLE,
                    }}
                  >
                    {currentStep === 0 ? (
                      <IdCard
                        variant="with-photo"
                        photoUrl={card.photoUrl}
                        hidePhotoBackground={card.hidePhotoBackground}
                        planName={card.petName}
                        ownerName={card.ownerName}
                        policyId={card.policyId}
                        startDate={card.startDate}
                        className="hidden max-w-83.5 md:flex"
                      />
                    ) : (
                      <IdCard
                        variant="no-photo"
                        policyId={card.policyId}
                        birthday={card.birthday}
                        weight={card.weightLabel}
                        vetName={card.vetName}
                        className="hidden max-w-83.5 md:flex"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden flex-col gap-6 md:flex">
              <H2 className="text-[3.125rem]! leading-15!">
                <Span className="text-neutral-900" textSize="inherit">
                  {t('claims.pet.titleStart')}
                </Span>
                <br />
                <Span className="text-indigo-base" textSize="inherit">
                  {t('claims.pet.titleHighlight')}
                </Span>
              </H2>
              <Text className="text-neutral-900">
                {t('claims.pet.description')}
              </Text>
            </div>
          </div>
          <div className="flex flex-1 flex-col">
            <Stepper
              items={steps}
              currentStep={currentStep}
              dynamicHeight
              className="md:gap-8"
            />
          </div>
        </div>

        <StickyFooter>
          {isLastStep ? (
            <>
              <Button
                variant="secondary"
                onClick={handleBack}
                className="h-12 w-1/2 px-2! md:w-auto md:px-8!"
                data-testid="pet-claim-back"
              >
                {t('claims.pet.back')}
              </Button>
              <Button
                onClick={handleNext}
                loading={isSubmitting}
                disabled={isSubmitting || isOffline}
                className="h-12 w-1/2 md:w-auto"
                data-testid="pet-claim-submit"
              >
                {t('claims.pet.form.save')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={handleCancel}
                className="h-12 w-1/2 px-2! md:w-auto md:px-8!"
                data-testid="pet-claim-cancel"
              >
                {t('claims.pet.cancel')}
              </Button>
              <Button
                onClick={handleNext}
                className="h-12 w-1/2 md:w-auto"
                data-testid="pet-claim-continue"
              >
                {t('claims.pet.continue')}
              </Button>
            </>
          )}
        </StickyFooter>
      </div>

      <ActionModal
        isOpen={submitModal.isOpen}
        variant={submitModal.variant}
        title={submitModal.title}
        description={submitModal.description}
        primaryAction={submitModal.primaryAction}
        secondaryAction={submitModal.secondaryAction}
        onClose={submitModal.onClose}
      >
        {submitModal.errorMessage && (
          <Text className="text-error-base text-sm!">
            {submitModal.errorMessage}
          </Text>
        )}
      </ActionModal>
    </FormProvider>
  )
}
